import Uppy, { UnknownProviderPlugin } from '@uppy/core';
import type { Provider } from '@uppy/companion-client';
import AwsS3 from '@uppy/aws-s3';
import GoldenRetriever from '@uppy/golden-retriever';
import GoogleDrive from '@uppy/google-drive';
import DropBox from '@uppy/dropbox';
import Box from '@uppy/box';
import { nanoid } from 'nanoid';
import ThumbnailGenerator from '@uppy/thumbnail-generator';
import OneDrive from '@uppy/onedrive';
import SharePoint from './SharePointPlugin';
import { nonNull } from '@helpers/non-null';
import { API_BASE_URL } from '@config/constants';
import { last100CorrelationIds } from '@api/client';
import {
  multipartUploadParts,
  multipartUploadPartsUploadParams,
  multipartUploadAbort,
  multipartUploadComplete
} from '@api/MultipartUpload';
import { initUploadAssetVersion } from '@api/Asset';

export function createAssetsUppy(
  userId: string,
  getAccessToken: () => Promise<string>
) {
  let sessionId = sessionStorage.getItem('assets-uppy-session-id');
  if (!sessionId) {
    sessionId = nanoid();
    sessionStorage.setItem('assets-uppy-session-id', sessionId);
  }
  const uppy = new Uppy({
    id: `assets-uppy_${userId}_${sessionId}`
  });
  initRemoteUploadPlugins(uppy, getAccessToken);
  uppy.use(GoldenRetriever, { serviceWorker: true } as any);
  uppy.use(ThumbnailGenerator, {
    thumbnailWidth: window.devicePixelRatio * 92,
    thumbnailHeight: window.devicePixelRatio * 64
  });
  uppy.use(AwsS3, {
    shouldUseMultipart: true,
    getChunkSize() {
      const MB = 1024 * 1024;
      return 5 * MB;
    },
    async createMultipartUpload(file) {
      const campaignId = file.meta.campaignId as string;
      const folderId = file.meta.folderId as string | null;
      const assetId = file.meta.assetId as string | null;
      const {
        data: { assetVersionId, uploadId, key }
      } = await initUploadAssetVersion({
        campaignId,
        target: assetId
          ? { type: 'asset', id: assetId }
          : folderId
          ? { type: 'folder', id: folderId }
          : null,
        fileName: nonNull(file.name),
        fileSize: nonNull(file.size)
      });
      file.meta.assetVersionId = assetVersionId;
      uppy.setFileMeta(file.id, { assetVersionId });
      return { uploadId, key };
    },
    async listParts(file, { key, uploadId }) {
      const {
        data: { parts }
      } = await multipartUploadParts({ key, uploadId: nonNull(uploadId) });
      return parts;
    },
    async signPart(file, { key, uploadId, partNumber }) {
      try {
        const {
          data: { presignedUrls, headers }
        } = await multipartUploadPartsUploadParams({
          key,
          uploadId,
          partNumber: [partNumber]
        });
        return { url: presignedUrls[partNumber], headers };
      } catch (err) {
        console.warn(err, '-> prepareUploadParts');
        throw err;
      }
    },
    async abortMultipartUpload(file, { key, uploadId }) {
      await multipartUploadAbort({ key, uploadId: nonNull(uploadId) });
    },
    async completeMultipartUpload(file, { key, uploadId, parts }) {
      const { data } = await multipartUploadComplete({
        key,
        uploadId,
        parts: parts.map((part) => ({
          PartNumber: nonNull(part.PartNumber),
          ETag: nonNull(part.ETag)
        }))
      });
      return data;
    }
  });
  return {
    uppy,
    userId
  };
}

function initRemoteUploadPlugins(
  uppy: Uppy<any, any>,
  getAccessToken: () => Promise<string>
) {
  const companionUrl = new URL('/asset/companion', API_BASE_URL).toString();
  const plugins = [
    { type: GoogleDrive, name: 'GoogleDrive' },
    { type: DropBox, name: 'Dropbox' },
    { type: Box, name: 'Box' },
    { type: OneDrive, name: 'OneDrive' },
    { type: SharePoint, name: 'SharePoint' }
  ];
  for (const { type, name } of plugins) {
    uppy.use(type, { companionUrl });
    const plugin = uppy.getPlugin(name) as UnknownProviderPlugin<any, any>;
    const provider = plugin.provider as Provider<any, any> | undefined;
    if (provider) {
      const headersFn = provider.headers;
      provider.headers = async () => {
        // Ensure app access token is passed to the companion API requests
        const headers = await headersFn.call(provider);
        const token = await getAccessToken();
        return {
          ...headers,
          Authorization: `Bearer ${token}`
        };
      };
    }
  }
  const { setFileState } = uppy;
  uppy.setFileState = (fileId, state) => {
    setFileState.call(uppy, fileId, state);
    if (state.serverToken) {
      // This is a workaround to set asset version id when uploading remote files with uppy companion.
      // For normal uploads, we set it in the createMultipartUpload method.
      // But this approach does not work for remote files uploaded with companion.
      // The serverToken is the uuid that identifies the upload session in companion.
      // On the server, we also use this uuid as an asset version id.
      uppy.setFileMeta(fileId, { assetVersionId: state.serverToken });

      // Also track server token as correlation id
      // To support "causedByMe" flag in websocket events
      if (last100CorrelationIds.length > 100) {
        last100CorrelationIds.shift();
      }
      last100CorrelationIds.push(state.serverToken);
    }
  };
}
